import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import api from '../services/api';

const AppAdmin = () => {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedTier, setSelectedTier] = useState('premium');

  useEffect(() => {
    checkAdminStatus();
    if (isAppAdmin) {
      fetchAllFamilies();
    }
  }, [isAppAdmin]);

  const checkAdminStatus = async () => {
    // Check if user is app admin by trying to access admin endpoint
    try {
      const response = await api.get('/families/admin/all/');
      setIsAppAdmin(true);
      setFamilies(response.data);
      setLoading(false);
    } catch (error) {
      setIsAppAdmin(false);
      setLoading(false);
    }
  };

  const fetchAllFamilies = async () => {
    try {
      const response = await api.get('/families/admin/all/');
      setFamilies(response.data);
    } catch (error) {
    }
  };

  const handleGrantComplimentary = async () => {
    if (!selectedFamily) return;

    try {
      await api.post(`/families/${selectedFamily.id}/admin/grant-complimentary/`, {
        tier: selectedTier,
      });
      await fetchAllFamilies();
      setGrantDialogOpen(false);
      setSelectedFamily(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to grant complimentary subscription');
    }
  };

  const handleRevokeComplimentary = async (family) => {
    if (!window.confirm(`Are you sure you want to revoke the complimentary subscription for ${family.name}?`)) {
      return;
    }

    try {
      await api.post(`/families/${family.id}/admin/revoke-complimentary/`);
      await fetchAllFamilies();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to revoke complimentary subscription');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!isAppAdmin) {
    return (
      <Container>
        <Alert severity="error">
          You do not have permission to access this page. Only app administrators can manage complimentary subscriptions.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
        <Typography variant="h4">App Administration</Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manage Complimentary Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Grant or revoke free premium/enterprise subscriptions to selected families.
          </Typography>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Family Name</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Current Tier</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Complimentary</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map((family) => {
              const owner = family.members?.find((m) => m.role === 'owner');
              return (
                <TableRow key={family.id}>
                  <TableCell>{family.name}</TableCell>
                  <TableCell>{owner?.user_name || 'Unknown'}</TableCell>
                  <TableCell>
                    <Chip
                      label={family.subscription_tier}
                      color={
                        family.subscription_tier === 'enterprise'
                          ? 'error'
                          : family.subscription_tier === 'premium'
                          ? 'primary'
                          : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {family.subscription_status || 'active'}
                  </TableCell>
                  <TableCell>
                    {family.is_complimentary ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Yes"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<CancelIcon />}
                        label="No"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{family.member_count || 0}</TableCell>
                  <TableCell>
                    {family.is_complimentary ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRevokeComplimentary(family)}
                      >
                        Revoke
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedFamily(family);
                          setGrantDialogOpen(true);
                        }}
                      >
                        Grant Free
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Complimentary Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Family: <strong>{selectedFamily?.name}</strong>
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                label="Subscription Tier"
              >
                <MenuItem value="premium">Premium ($9.99/month value)</MenuItem>
                <MenuItem value="enterprise">Enterprise ($29.99/month value)</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 2 }}>
              This will grant a free subscription that never expires. The family will have full access to all features
              of the selected tier without payment.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGrantComplimentary} variant="contained" color="primary">
            Grant Subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AppAdmin;
